// API Client for frontend components

import { ApiResponse } from './api-helpers';
import {
  Tenant, Customer, Rag, Tool, PortalUser, Widget,
  Conversation, SessionUser, CreateCustomerRequest,
  CreateRagRequest, UpdateRagRequest,
  CreateWidgetRequest, UpdateWidgetRequest,
  GroupedTools, ToolStatus, PricingTier,
  RagApiKey, CreateRagApiKeyRequest,
  WebSourcesResponse, WebSourceDiscoverRequest, WebSourceDiscoverResponse,
  WebSourceScrapeRequest, ScrapeEvent, DiscoveryEvent,
  GroupedAgentTools, AgentToolConfig, AgentToolWithConfig,
  AgentFaqEntry,
} from './types';

const API_BASE = '/api';

// Get auth token from storage (checks both localStorage and sessionStorage)
export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
}

// Set auth token (supports both localStorage and sessionStorage)
export function setToken(token: string, rememberMe: boolean = true): void {
  if (rememberMe) {
    localStorage.setItem('auth_token', token);
    sessionStorage.removeItem('auth_token'); // Clear from session storage if it was there
  } else {
    sessionStorage.setItem('auth_token', token);
    localStorage.removeItem('auth_token'); // Clear from local storage if it was there
  }
}

// Clear auth token from both storage types
export function clearToken(): void {
  localStorage.removeItem('auth_token');
  sessionStorage.removeItem('auth_token');
}

// Get session user from storage (checks both localStorage and sessionStorage)
export function getSessionUser(): SessionUser | null {
  if (typeof window === 'undefined') return null;

  const sessionStr = localStorage.getItem('session_user') || sessionStorage.getItem('session_user');
  if (!sessionStr) {
    return null;
  }
  try {
    const user = JSON.parse(sessionStr) as SessionUser;
    return user;
  } catch (e) {
    console.error('[getSessionUser] Failed to parse session user from storage:', e);
    return null;
  }
}

// Set session user (supports both localStorage and sessionStorage)
export function setSessionUser(user: SessionUser, rememberMe: boolean = true): void {
  if (rememberMe) {
    localStorage.setItem('session_user', JSON.stringify(user));
    sessionStorage.removeItem('session_user'); // Clear from session storage if it was there
  } else {
    sessionStorage.setItem('session_user', JSON.stringify(user));
    localStorage.removeItem('session_user'); // Clear from local storage if it was there
  }
}

// Clear session from both storage types
export function clearSession(): void {
  clearToken();
  localStorage.removeItem('session_user');
  sessionStorage.removeItem('session_user');
}

// Generic fetch helper
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const data: ApiResponse<T> = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'An error occurred');
  }

  return data.data as T;
}

// ==========================================
// Auth API
// ==========================================
export interface LoginChallenge {
  challengeName: 'NEW_PASSWORD_REQUIRED';
  session: string;
  email: string;
}

export async function login(email: string, password: string, rememberMe: boolean = true): Promise<{ user: SessionUser; token: string } | LoginChallenge> {
  const result = await apiFetch<any>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

  console.log('Login result:', result);

  // Check if this is a password change challenge
  if (result?.challengeName === 'NEW_PASSWORD_REQUIRED') {
    // Return challenge instead of throwing error for cleaner handling
    return {
      challengeName: 'NEW_PASSWORD_REQUIRED',
      session: result.session,
      email: result.email,
    };
  }

  // API returns { user, token } after apiFetch unwraps it
  if (!result || !result.user || !result.token) {
    console.error('Invalid login response:', result);
    throw new Error('Invalid response from server');
  }

  const { user, token } = result;

  setToken(token, rememberMe);
  setSessionUser(user, rememberMe);

  return { user, token };
}

export async function changePassword(
  email: string,
  newPassword: string,
  session: string,
  given_name?: string,
  family_name?: string
): Promise<{ message: string; tokens: any; user?: SessionUser }> {
  const result = await apiFetch<{ message: string; tokens: any }>('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ email, newPassword, session, given_name, family_name }),
  });

  // If successful, the user should be able to login immediately
  // Try to log them in with the new password
  try {
    const loginResult = await login(email, newPassword);
    return {
      ...result,
      user: loginResult.user,
    };
  } catch (loginError) {
    console.error('Auto-login after password change failed:', loginError);
    // Return the result anyway, let the caller handle re-login
    return result;
  }
}

export async function forgotPassword(email: string): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(email: string, verificationCode: string, newPassword: string): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ email, verificationCode, newPassword }),
  });
}

export async function getSession(): Promise<{ user: SessionUser }> {
  return apiFetch<{ user: SessionUser }>('/auth/session');
}

export async function impersonate(customerId: string | null): Promise<void> {
  await apiFetch('/auth/impersonate', {
    method: 'POST',
    body: JSON.stringify({ customer_id: customerId }),
  });

  // Refresh session to get updated user
  const { user } = await getSession();
  setSessionUser(user);
}

// V2 impersonation API - supports ROOT→TENANT and TENANT→CUSTOMER
export async function impersonateV2(targetType: 'TENANT' | 'CUSTOMER', targetId: string): Promise<void> {
  await apiFetch('/auth/impersonate/v2', {
    method: 'POST',
    body: JSON.stringify({ target_type: targetType, target_id: targetId }),
  });

  // Refresh session to get updated user
  const { user } = await getSession();
  setSessionUser(user);
}

// Stop impersonation (v2 API)
export async function stopImpersonation(level?: 'CUSTOMER' | 'TENANT' | 'ALL'): Promise<void> {
  const url = level ? `/auth/impersonate/v2?level=${level}` : '/auth/impersonate/v2';
  await apiFetch(url, {
    method: 'DELETE',
  });

  // Refresh session to get updated user
  const { user } = await getSession();
  setSessionUser(user);
}

export function logout(): void {
  clearSession();
}

// ==========================================
// Invitations API
// ==========================================

export interface CreateInvitationRequest {
  email: string;
  role_level?: 'USER' | 'ADMIN';
  user_type?: 'AGENT' | 'ROOT';  // For ROOT inviting AGENT or ROOT users
  target_tenant_id?: string;  // For ROOT inviting to existing tenant
  tenant_name?: string;  // For ROOT inviting new tenant signup
  rag_id?: string;  // Agent-scoped invite
  customer_id?: string;  // For TENANT creating CUSTOMER invitation
  // Domain restrictions for new tenant invites
  email_domain_restriction_enabled?: boolean;
  allowed_email_domains?: string[];
}

export interface InvitationResponse {
  invitation?: {
    invitation_id: number;
    email: string;
    user_type: string;
    role_level: string;
    tenant_name: string;
    customer_name: string | null;
    expires_at: string;
  };
  signup_url?: string | null;
  association_created?: boolean;
  portal_user?: {
    user_id: number;
    email: string;
    user_type: string;
    role_level: string;
    tenant_id?: string | null;
    customer_id?: string | null;
  };
  message?: string;
}

export async function createInvitation(data: CreateInvitationRequest): Promise<InvitationResponse> {
  return apiFetch<InvitationResponse>('/invitations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getInvitations(
  status?: 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED',
  ragId?: string
): Promise<any[]> {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (ragId) params.set('rag_id', ragId);
  const query = params.toString();
  const url = query ? `/invitations?${query}` : '/invitations';
  const result = await apiFetch<{ invitations: any[] }>(url);
  return result.invitations;
}

export async function deleteInvitation(invitationId: string): Promise<{ message: string }> {
  return apiFetch<{ message: string }>(`/invitations?invitation_id=${invitationId}`, {
    method: 'DELETE',
  });
}

export async function resendInvitation(invitationId: string): Promise<{ message: string; email: string }> {
  return apiFetch<{ message: string; email: string }>('/invitations/resend', {
    method: 'POST',
    body: JSON.stringify({ invitation_id: invitationId }),
  });
}

export async function getRagUsers(ragId: string): Promise<any[]> {
  const result = await apiFetch<{ users: any[] }>(`/rags/${ragId}/users`);
  return result.users;
}

export async function deleteUser(userId: string): Promise<{ message: string }> {
  return apiFetch<{ message: string }>(`/users/${userId}`, {
    method: 'DELETE',
  });
}

export async function deletePortalUser(userId: string): Promise<{ message: string }> {
  return apiFetch<{ message: string }>(`/portal-users/${userId}`, {
    method: 'DELETE',
  });
}

/**
 * Remove a user's access to a specific RAG
 * For AGENT/AGENT_USER: Only deletes account if this is their last RAG access
 */
export async function removeRagAccess(
  ragId: string,
  userId: string
): Promise<{ message: string; account_deleted: boolean }> {
  return apiFetch<{ message: string; account_deleted: boolean }>(`/rags/${ragId}/users/${userId}`, {
    method: 'DELETE',
  });
}

// ==========================================
// Tenants API
// ==========================================
export async function getTenants(): Promise<Array<Tenant & { customer_count: number; rag_count: number; total_credit_balance: string }>> {
  const result = await apiFetch<{ tenants: Array<Tenant & { customer_count: number; rag_count: number; total_credit_balance: string }> }>('/tenants');
  return result.tenants;
}

export async function getTenant(tenantId: string): Promise<Tenant> {
  return apiFetch<Tenant>(`/tenants/${tenantId}`);
}

export async function createTenant(data: { name: string; slug?: string; logo_url?: string }): Promise<Tenant> {
  return apiFetch<Tenant>('/tenants', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateTenant(tenantId: string, data: Partial<Tenant>): Promise<Tenant> {
  return apiFetch<Tenant>(`/tenants/${tenantId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// ==========================================
// Customers API
// ==========================================
export async function getCustomers(tenantId?: string): Promise<Customer[]> {
  const params = tenantId ? `?tenant_id=${tenantId}` : '';
  return apiFetch<Customer[]>(`/customers${params}`);
}

export async function getCustomer(customerId: string): Promise<Customer> {
  return apiFetch<Customer>(`/customers/${customerId}`);
}

export async function createCustomer(tenantId: string, data: CreateCustomerRequest): Promise<Customer> {
  return apiFetch<Customer>('/customers', {
    method: 'POST',
    body: JSON.stringify({ ...data, tenant_id: tenantId }),
  });
}

export async function updateCustomer(customerId: string, data: Partial<Customer>): Promise<Customer> {
  return apiFetch<Customer>(`/customers/${customerId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteCustomer(customerId: string): Promise<void> {
  await apiFetch(`/customers/${customerId}`, { method: 'DELETE' });
}

export async function addCredits(customerId: string, amount: number): Promise<{ new_balance: number }> {
  return apiFetch<{ new_balance: number }>(`/customers/${customerId}/credits`, {
    method: 'POST',
    body: JSON.stringify({ amount }),
  });
}

export async function getCreditsHistory(customerId: string): Promise<{
  credit_balance: number;
  total_spent: number;
  recent_costs: unknown[];
}> {
  return apiFetch(`/customers/${customerId}/credits`);
}

// ==========================================
// RAGs API
// ==========================================
export async function getRags(customerId?: string, tenantId?: string): Promise<Rag[]> {
  const params = new URLSearchParams();
  if (customerId) params.set('customer_id', customerId);
  else if (tenantId) params.set('tenant_id', tenantId);

  const queryString = params.toString();
  return apiFetch<Rag[]>(`/rags${queryString ? `?${queryString}` : ''}`);
}

export async function getRagsByIds(ids: string[]): Promise<Rag[]> {
  if (ids.length === 0) {
    return [];
  }
  return apiFetch<Rag[]>(`/rags?ids=${ids.join(',')}`);
}

export async function getRag(ragId: string): Promise<Rag> {
  return apiFetch<Rag>(`/rags/${ragId}`);
}

export async function createRag(data: CreateRagRequest): Promise<Rag> {
  return apiFetch<Rag>('/rags', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRag(ragId: string, data: UpdateRagRequest): Promise<Rag> {
  return apiFetch<Rag>(`/rags/${ragId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteRag(ragId: string): Promise<void> {
  await apiFetch(`/rags/${ragId}`, { method: 'DELETE' });
}

// RAG API Keys (multiple keys per RAG)
export async function getRagApiKeys(ragId: string): Promise<RagApiKey[]> {
  return apiFetch<RagApiKey[]>(`/rags/${ragId}/api-keys`);
}

export async function createRagApiKey(ragId: string, data: CreateRagApiKeyRequest): Promise<RagApiKey> {
  return apiFetch<RagApiKey>(`/rags/${ragId}/api-keys`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteRagApiKey(ragId: string, keyId: string): Promise<void> {
  await apiFetch(`/rags/${ragId}/api-keys/${keyId}`, { method: 'DELETE' });
}

export async function regenerateRagApiKeyById(ragId: string, keyId: string): Promise<RagApiKey> {
  return apiFetch<RagApiKey>(`/rags/${ragId}/api-keys/${keyId}`, {
    method: 'POST',
  });
}

// ==========================================
// Widgets API
// ==========================================
export interface WidgetsListOptions {
  includeInactive?: boolean;
}

export async function getWidgets(customerId?: string, tenantId?: string, options?: WidgetsListOptions): Promise<Widget[]> {
  const params = new URLSearchParams();
  if (customerId) params.set('customer_id', customerId);
  else if (tenantId) params.set('tenant_id', tenantId);
  if (options?.includeInactive) params.set('include_inactive', 'true');

  const queryString = params.toString();
  return apiFetch<Widget[]>(`/widgets${queryString ? `?${queryString}` : ''}`);
}

export async function getWidget(widgetId: string): Promise<Widget> {
  return apiFetch<Widget>(`/widgets/${widgetId}`);
}

export async function createWidget(data: CreateWidgetRequest): Promise<Widget> {
  return apiFetch<Widget>('/widgets', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateWidget(widgetId: string, data: UpdateWidgetRequest): Promise<Widget> {
  return apiFetch<Widget>(`/widgets/${widgetId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteWidget(widgetId: string): Promise<void> {
  await apiFetch(`/widgets/${widgetId}`, { method: 'DELETE' });
}

// ==========================================
// Tools API
// ==========================================

export interface ToolsListOptions {
  includeInactive?: boolean;
  grouped?: boolean;
  status?: ToolStatus;
  type?: 'language' | 'embedding' | 'image';
}

// Get tools as flat list
export async function getTools(options?: ToolsListOptions): Promise<Tool[]> {
  const params = new URLSearchParams();
  if (options?.includeInactive) params.set('include_inactive', 'true');
  if (options?.status) params.set('status', options.status);
  if (options?.type) params.set('type', options.type);
  const queryString = params.toString();
  return apiFetch<Tool[]>(`/tools${queryString ? `?${queryString}` : ''}`);
}

// Get tools grouped by provider (for ToolSelector)
export async function getGroupedTools(options?: Omit<ToolsListOptions, 'grouped'>): Promise<GroupedTools> {
  const params = new URLSearchParams();
  params.set('grouped', 'true');
  if (options?.includeInactive) params.set('include_inactive', 'true');
  if (options?.status) params.set('status', options.status);
  if (options?.type) params.set('type', options.type);
  return apiFetch<GroupedTools>(`/tools?${params.toString()}`);
}

export async function getTool(toolId: number): Promise<Tool> {
  return apiFetch<Tool>(`/tools/${toolId}`);
}

// Update tool (status, pricing tier, etc.)
export async function updateTool(
  toolId: number,
  data: { status?: ToolStatus; pricing_tier?: PricingTier; name?: string; description?: string }
): Promise<Tool> {
  return apiFetch<Tool>(`/tools/${toolId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// Sync tools from Vercel AI Gateway (admin only)
export interface SyncResult {
  message: string;
  sync: {
    fetched: number;
    inserted: number;
    updated: number;
    decommissioned: number;
  };
  totals: Record<string, number>;
}

export async function syncTools(): Promise<SyncResult> {
  return apiFetch<SyncResult>('/tools/sync', {
    method: 'POST',
  });
}

// ==========================================
// Portal Users API
// ==========================================
export async function getPortalUsers(tenantId?: string, customerId?: string, userType?: string): Promise<PortalUser[]> {
  const params = new URLSearchParams();
  if (customerId) params.set('customer_id', customerId);
  else if (tenantId) params.set('tenant_id', tenantId);
  else if (userType) params.set('user_type', userType);

  const queryString = params.toString();
  return apiFetch<PortalUser[]>(`/portal-users${queryString ? `?${queryString}` : ''}`);
}

export async function createPortalUser(data: Partial<PortalUser>): Promise<PortalUser> {
  return apiFetch<PortalUser>('/portal-users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ==========================================
// Conversations API
// ==========================================
export async function getConversations(ragId?: string, customerId?: string): Promise<Conversation[]> {
  const params = new URLSearchParams();
  if (ragId) params.set('rag_id', ragId);
  else if (customerId) params.set('customer_id', customerId);

  const queryString = params.toString();
  return apiFetch<Conversation[]>(`/conversations${queryString ? `?${queryString}` : ''}`);
}

export async function getConversation(conversationId: string): Promise<Conversation> {
  return apiFetch<Conversation>(`/conversations/${conversationId}`);
}

// ==========================================
// Analytics API
// ==========================================
export interface AnalyticsData {
  period_days: number;
  conversations: {
    total: number;
    total_messages: number;
    avg_messages: string;
  };
  costs: {
    total: number;
    total_tokens: number;
    input_tokens: number;
    output_tokens: number;
  };
  daily_usage: Array<{
    date: string;
    conversations: number;
    messages: number;
    cost: number;
  }>;
  model_usage: Array<{
    model_id: string;
    usage_count: number;
    total_cost: number;
  }>;
}

export async function getAnalytics(
  params: { customer_id?: string; rag_id?: string; tenant_id?: string; days?: number }
): Promise<AnalyticsData> {
  const searchParams = new URLSearchParams();
  if (params.customer_id) searchParams.set('customer_id', params.customer_id);
  if (params.rag_id) searchParams.set('rag_id', params.rag_id);
  if (params.tenant_id) searchParams.set('tenant_id', params.tenant_id);
  if (params.days) searchParams.set('days', params.days.toString());

  return apiFetch<AnalyticsData>(`/analytics?${searchParams.toString()}`);
}

// ==========================================
// Tenant Dashboard API
// ==========================================
export interface TenantDashboardStats {
  metrics: {
    totalCustomers: { value: number; change: number | null };
    activeCustomers: { value: number; change: number | null };
    totalRags: { value: number; change: number | null };
    totalCredits: { value: number; change: number | null };
    totalConversations: { value: number; change: number | null };
  };
  chartData: Array<{
    month: string;
    customers: number;
    rags: number;
    revenue: number;
  }>;
}

export async function getTenantDashboardStats(tenantId: string): Promise<TenantDashboardStats> {
  return apiFetch<TenantDashboardStats>(`/tenant-dashboard/stats?tenant_id=${tenantId}`);
}

// ==========================================
// Audit Log API
// ==========================================
export interface AuditLogEntry {
  id: string;
  type: string;
  entityType: string;
  entityName: string;
  description: string;
  timestamp: string;
  userEmail: string | null;
}

export async function getAuditLog(
  tenantId?: string,
  limit?: number,
  offset?: number,
  sortOrder?: 'asc' | 'desc'
): Promise<AuditLogEntry[]> {
  const params = new URLSearchParams();
  if (tenantId) params.set('tenant_id', tenantId);
  if (limit) params.set('limit', limit.toString());
  if (offset !== undefined) params.set('offset', offset.toString());
  if (sortOrder) params.set('sort_order', sortOrder);
  const queryString = params.toString();
  return apiFetch<AuditLogEntry[]>(`/audit-log${queryString ? `?${queryString}` : ''}`);
}

// ==========================================
// RAG Stats API
// ==========================================
export interface RagStats {
  ragId: string;
  name: string;
  status: string;
  createdAt: string;
  modelName: string;
  stats: {
    totalQueries: number;
    totalMessages: number;
    queriesToday: number;
    queriesThisWeek: number;
    avgResponseTime: string;
    successRate: number;
    totalCost: number;
    costToday: number;
    costThisWeek: number;
    totalTokens: number;
    creditBalance: number;
  };
}

export async function getRagStats(ragId: string): Promise<RagStats> {
  return apiFetch<RagStats>(`/rags/${ragId}/stats`);
}

// ==========================================
// RAG Storage API (Secure proxy - no credentials exposed to frontend)
// ==========================================
export interface StorageObject {
  key: string;
  size: number;
  lastModified: string;
  isFolder: boolean;
}

export interface StorageListResponse {
  objects: StorageObject[];
  prefix: string;
}

export interface StorageUploadResponse {
  uploadUrl: string;
  key: string;
}

export interface StorageDownloadResponse {
  downloadUrl: string;
  key: string;
}

export interface StorageDeleteResponse {
  message: string;
  deletedKeys: string[];
}

// List objects in storage
export async function listStorageObjects(ragId: string, prefix: string = ''): Promise<StorageListResponse> {
  return apiFetch<StorageListResponse>(`/rags/${ragId}/storage`, {
    method: 'POST',
    body: JSON.stringify({ prefix }),
  });
}

// Get presigned URL for upload
export async function getStorageUploadUrl(
  ragId: string,
  filename: string,
  contentType: string,
  prefix: string = ''
): Promise<StorageUploadResponse> {
  return apiFetch<StorageUploadResponse>(`/rags/${ragId}/storage/upload`, {
    method: 'POST',
    body: JSON.stringify({ filename, contentType, prefix }),
  });
}

// Get presigned URL for download
export async function getStorageDownloadUrl(ragId: string, key: string): Promise<StorageDownloadResponse> {
  return apiFetch<StorageDownloadResponse>(`/rags/${ragId}/storage/download`, {
    method: 'POST',
    body: JSON.stringify({ key }),
  });
}

// Delete objects from storage
export async function deleteStorageObjects(ragId: string, keys: string[]): Promise<StorageDeleteResponse> {
  return apiFetch<StorageDeleteResponse>(`/rags/${ragId}/storage/delete`, {
    method: 'POST',
    body: JSON.stringify({ keys }),
  });
}

// ==========================================
// RAG Usage API
// ==========================================
export interface RagUsageData {
  dailyUsage: Array<{
    date: string;
    conversations: number;
    messages: number;
    cost: number;
    tokens: number;
  }>;
  heatmapData: Array<{
    day: string;
    hour: number;
    value: number;
  }>;
}

export async function getRagUsage(ragId: string, days?: number): Promise<RagUsageData> {
  const params = days ? `?days=${days}` : '';
  return apiFetch<RagUsageData>(`/rags/${ragId}/usage${params}`);
}

// ==========================================
// RAG Transactions API
// ==========================================
export interface RagTransaction {
  id: string;
  type: 'usage' | 'topup';
  amount: number;
  description: string;
  timestamp: string;
  balance: number;
}

export interface RagTransactionsData {
  transactions: RagTransaction[];
  currentBalance: number;
  totalSpent: number;
}

export async function getRagTransactions(
  ragId: string,
  limit?: number,
  offset?: number
): Promise<RagTransactionsData> {
  const params = new URLSearchParams();
  if (limit) params.set('limit', limit.toString());
  if (offset) params.set('offset', offset.toString());
  const queryString = params.toString();
  return apiFetch<RagTransactionsData>(`/rags/${ragId}/transactions${queryString ? `?${queryString}` : ''}`);
}

// ==========================================
// Customer Stats API
// ==========================================
export interface CustomerStats {
  customerId: string;
  name: string;
  accountStatus: 'active' | 'warning' | 'suspended' | 'needs_credits';
  summary: {
    totalCredits: number;
    totalUsage: number;
    totalTokens: number;
    totalConversations: number;
    activeRAGs: number;
    maxRags: number;
  };
  ragStats: Array<{
    ragId: string;
    name: string;
    status: string;
    totalCost: number;
    totalTokens: number;
    conversationCount: number;
    lastActivity: string;
  }>;
}

export async function getCustomerStats(customerId: string): Promise<CustomerStats> {
  return apiFetch<CustomerStats>(`/customers/${customerId}/stats`);
}

// ==========================================
// Web Sources API
// ==========================================

export async function getWebSources(ragId: string): Promise<WebSourcesResponse> {
  return apiFetch<WebSourcesResponse>(`/rags/${ragId}/web-sources`);
}

export async function discoverWebPages(
  ragId: string,
  data: WebSourceDiscoverRequest
): Promise<WebSourceDiscoverResponse> {
  return apiFetch<WebSourceDiscoverResponse>(`/rags/${ragId}/web-sources/discover`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Discover pages with streaming response
export async function discoverWebPagesStreaming(
  ragId: string,
  data: WebSourceDiscoverRequest,
  onProgress: (event: DiscoveryEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  const token = getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/rags/${ragId}/web-sources/discover`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Discovery failed: ${response.status} - ${errorText}`);
  }

  if (!response.body) {
    throw new Error('No response body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');

      // Keep the last incomplete line in the buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.trim()) {
          try {
            const event = JSON.parse(line) as DiscoveryEvent;
            onProgress(event);
          } catch (e) {
            console.error('Failed to parse discovery event:', line, e);
          }
        }
      }
    }

    // Process any remaining data in buffer
    if (buffer.trim()) {
      try {
        const event = JSON.parse(buffer) as DiscoveryEvent;
        onProgress(event);
      } catch (e) {
        console.error('Failed to parse final discovery event:', buffer, e);
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// Scrape pages with streaming response
export async function scrapeWebPages(
  ragId: string,
  data: WebSourceScrapeRequest,
  onProgress: (event: ScrapeEvent) => void
): Promise<void> {
  const token = getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/rags/${ragId}/web-sources/scrape`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Scrape failed: ${response.status}`);
  }

  if (!response.body) {
    throw new Error('No response body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');

    // Keep the last incomplete line in the buffer
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.trim()) {
        try {
          const event = JSON.parse(line) as ScrapeEvent;
          onProgress(event);
        } catch (e) {
          console.error('Failed to parse scrape event:', line, e);
        }
      }
    }
  }

  // Process any remaining data in buffer
  if (buffer.trim()) {
    try {
      const event = JSON.parse(buffer) as ScrapeEvent;
      onProgress(event);
    } catch (e) {
      console.error('Failed to parse final scrape event:', buffer, e);
    }
  }
}

export async function deleteWebDomain(ragId: string, domain: string): Promise<void> {
  await apiFetch(`/rags/${ragId}/web-sources/domain/${encodeURIComponent(domain)}`, {
    method: 'DELETE',
  });
}

export async function deleteWebPage(ragId: string, docId: string): Promise<void> {
  await apiFetch(`/rags/${ragId}/web-sources/page/${encodeURIComponent(docId)}`, {
    method: 'DELETE',
  });
}

// ==========================================
// Agent Tools API
// ==========================================

export interface AgentToolsResponse {
  tools: GroupedAgentTools[];
  max_tool_steps: number;
}

export interface UpdateToolConfigResponse {
  success: boolean;
  config: AgentToolConfig;
}

// Get all available tools and their configuration for an agent
export async function getAgentTools(ragId: string): Promise<AgentToolsResponse> {
  return apiFetch<AgentToolsResponse>(`/rags/${ragId}/tools`);
}

// Update agent's max_tool_steps setting
export async function updateAgentToolSettings(
  ragId: string,
  maxToolSteps: number
): Promise<{ success: boolean; max_tool_steps: number }> {
  return apiFetch(`/rags/${ragId}/tools`, {
    method: 'PATCH',
    body: JSON.stringify({ max_tool_steps: maxToolSteps }),
  });
}

// Enable or disable a tool for an agent
export async function updateAgentToolConfig(
  ragId: string,
  toolId: string,
  options: {
    enabled?: boolean;
    needs_approval?: boolean;
    config?: Record<string, unknown>;
  }
): Promise<UpdateToolConfigResponse> {
  return apiFetch<UpdateToolConfigResponse>(`/rags/${ragId}/tools/${toolId}`, {
    method: 'PUT',
    body: JSON.stringify(options),
  });
}

// Remove tool configuration from an agent
export async function removeAgentToolConfig(ragId: string, toolId: string): Promise<void> {
  await apiFetch(`/rags/${ragId}/tools/${toolId}`, {
    method: 'DELETE',
  });
}

// ==========================================
// FAQ Canned Responses API
// ==========================================

export interface FaqEntriesResponse {
  entries: AgentFaqEntry[];
  count: number;
  max: number;
}

// Get all FAQ entries for an agent
export async function getFaqEntries(ragId: string): Promise<FaqEntriesResponse> {
  return apiFetch<FaqEntriesResponse>(`/rags/${ragId}/faq`);
}

// Create a new FAQ entry
export async function createFaqEntry(
  ragId: string,
  data: { question: string; answer: string; sort_order?: number }
): Promise<AgentFaqEntry> {
  return apiFetch<AgentFaqEntry>(`/rags/${ragId}/faq`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Update an FAQ entry
export async function updateFaqEntry(
  ragId: string,
  faqId: string,
  data: { question?: string; answer?: string; sort_order?: number }
): Promise<AgentFaqEntry> {
  return apiFetch<AgentFaqEntry>(`/rags/${ragId}/faq/${faqId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Delete an FAQ entry
export async function deleteFaqEntry(ragId: string, faqId: string): Promise<void> {
  await apiFetch(`/rags/${ragId}/faq/${faqId}`, {
    method: 'DELETE',
  });
}

// Bulk reorder FAQ entries
export async function reorderFaqEntries(
  ragId: string,
  entries: Array<{ id: string; sort_order: number }>
): Promise<void> {
  await apiFetch(`/rags/${ragId}/faq`, {
    method: 'PATCH',
    body: JSON.stringify({ entries }),
  });
}

// ============================================================
// Auth Context Switching
// ============================================================

export interface UserContext {
  user_id: string;
  user_type: string;
  is_primary: boolean;
  rag_count: number;
  tenant_name: string | null;
  customer_name: string | null;
}

export interface UserContextsResponse {
  current_user_id: string;
  current_user_type: string;
  contexts: UserContext[];
}

/**
 * Get all available user contexts for the current user
 */
export async function getUserContexts(): Promise<UserContextsResponse> {
  return await apiFetch<UserContextsResponse>('/auth/contexts');
}

/**
 * Switch to a different user context
 */
export async function switchContext(user_type: string): Promise<{ user: SessionUser; token: string }> {
  const result = await apiFetch<{ user: SessionUser; token: string }>('/auth/switch-context', {
    method: 'POST',
    body: JSON.stringify({ user_type }),
  });

  // Preserve the rememberMe preference (check which storage is currently in use)
  const rememberMe = typeof window !== 'undefined' && localStorage.getItem('auth_token') !== null;

  // Update storage with new token and session, preserving rememberMe preference
  if (result.token) {
    setToken(result.token, rememberMe);
  }
  if (result.user) {
    setSessionUser(result.user, rememberMe);
  }

  return result;
}

// ==========================================
// Tenant Subscription API
// ==========================================

export interface SubscriptionDetails {
  tenant: {
    tenant_id: string;
    name: string;
    stripe_customer_id?: string;
    stripe_subscription_id?: string;
    subscription_status?: string;
    billing_email?: string;
    trial_ends_at?: string;
    subscription_current_period_end?: string;
  };
  agentCount: number;
  stripeSubscription: {
    id: string;
    status: string;
    current_period_end: number;
    cancel_at_period_end: boolean;
    items: {
      data: Array<{
        price: {
          id: string;
          unit_amount: number;
          currency: string;
        };
        quantity: number;
      }>;
    };
  } | null;
  hasPaymentSetup: boolean;
  hasActiveSubscription: boolean;
  subscriptionStatus?: string;
}

export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export interface BillingPortalResponse {
  url: string;
}

/**
 * Get subscription details for a tenant
 */
export async function getTenantSubscription(tenantId: string): Promise<SubscriptionDetails> {
  return apiFetch<SubscriptionDetails>(`/tenants/${tenantId}/subscription`);
}

/**
 * Create a Stripe checkout session for payment setup
 */
export async function createSubscriptionCheckout(
  tenantId: string,
  returnUrl?: string
): Promise<CheckoutSessionResponse> {
  return apiFetch<CheckoutSessionResponse>(`/tenants/${tenantId}/subscription/checkout`, {
    method: 'POST',
    body: JSON.stringify({ returnUrl }),
  });
}

/**
 * Create a Stripe billing portal session for subscription management
 */
export async function createBillingPortal(
  tenantId: string,
  returnUrl?: string
): Promise<BillingPortalResponse> {
  return apiFetch<BillingPortalResponse>(`/tenants/${tenantId}/subscription/portal`, {
    method: 'POST',
    body: JSON.stringify({ returnUrl }),
  });
}

export interface SubscriptionPricing {
  priceId: string;
  unitAmount: number; // Amount in smallest currency unit (e.g., pence)
  currency: string;
  interval: string;
  intervalCount: number;
}

export async function getSubscriptionPricing(): Promise<SubscriptionPricing> {
  return apiFetch<SubscriptionPricing>('/subscription/pricing', {
    method: 'GET',
  });
}

// ==========================================
// Conversation History
// ==========================================

export interface ConversationMessage {
  role: string;
  content: string;
  timestamp: string;
  tool_calls?: Array<{
    id: string;
    name: string;
    input: Record<string, unknown>;
    output?: unknown;
  }>;
}

export interface ConversationHistoryItem {
  conversation_id: string;
  session_id: string;
  messages: ConversationMessage[];
  visitor_email: string | null;
  visitor_first_name: string | null;
  visitor_last_name: string | null;
  visitor_metadata: Record<string, unknown> | null;
  message_count: number;
  conversation_title: string | null;
  started_at: string;
  last_message_at: string;
  portal_user_id: number | null;  // Added: ID of authenticated portal user (if any)
  widget_user_email: string | null;
  widget_user_display_name: string | null;
}

export interface ConversationHistoryResponse {
  conversations: ConversationHistoryItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface ConversationFilters {
  limit?: number;
  offset?: number;
  hasMessages?: boolean;
  search?: string;
  startDate?: string; // ISO date string
  endDate?: string; // ISO date string
  minMessages?: number;
  maxMessages?: number;
  userType?: 'authenticated' | 'anonymous';
}

/**
 * Get conversation history for a RAG
 */
export async function getRagConversations(
  ragId: string,
  options?: ConversationFilters
): Promise<ConversationHistoryResponse> {
  const params = new URLSearchParams();
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.offset) params.append('offset', options.offset.toString());
  if (options?.hasMessages) params.append('has_messages', 'true');
  if (options?.search) params.append('search', options.search);
  if (options?.startDate) params.append('start_date', options.startDate);
  if (options?.endDate) params.append('end_date', options.endDate);
  if (options?.minMessages) params.append('min_messages', options.minMessages.toString());
  if (options?.maxMessages) params.append('max_messages', options.maxMessages.toString());
  if (options?.userType) params.append('user_type', options.userType);

  const url = `/rags/${ragId}/conversations${params.toString() ? `?${params.toString()}` : ''}`;
  const result = await apiFetch<ConversationHistoryResponse>(url);
  return result;
}

// ===== CREDIT PURCHASES =====

export interface CreditPackage {
  credits: number;
  displayAmount: string;
}

export interface CreditPackagesResponse {
  packages: CreditPackage[];
  currency: string;
  conversionRate: number;
}

export interface CreditPurchase {
  purchase_id: string;
  credits_purchased: string;
  amount_paid: number;
  currency: string;
  status: string;
  created_at: string;
  completed_at: string | null;
  refunded_at: string | null;
}

export interface CreditHistoryResponse {
  purchases: CreditPurchase[];
}

export interface CreateCreditCheckoutRequest {
  creditsAmount: number;
  returnUrl?: string;
}

export interface CreateCreditCheckoutResponse {
  sessionId: string;
  url: string;
}

/**
 * Get available credit packages
 */
export async function getCreditPackages(customerId: string): Promise<CreditPackagesResponse> {
  return apiFetch<CreditPackagesResponse>(`/customers/${customerId}/credits/packages`);
}

/**
 * Get credit purchase history
 */
export async function getCreditHistory(customerId: string): Promise<CreditHistoryResponse> {
  return apiFetch<CreditHistoryResponse>(`/customers/${customerId}/credits/history`);
}

/**
 * Create a Stripe checkout session for credit purchase
 */
export async function createCreditCheckout(
  customerId: string,
  request: CreateCreditCheckoutRequest
): Promise<CreateCreditCheckoutResponse> {
  return apiFetch<CreateCreditCheckoutResponse>(`/customers/${customerId}/credits/checkout`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}
